import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { syncWithFdsAdmin } from 'services/users'
import { useNotification } from 'components/Notification'
import { USERS_QUERY_KEY } from './useUsers'

/**
 * Mutation hook for syncing a user with FDS Admin.
 * Tracks which row is currently syncing via syncingRowId.
 * @param {boolean} shouldRefreshUsersList - Whether to invalidate the users query on success
 * @returns {object} - { syncUser, syncUserForRow, isSyncing, syncingRowId }
 */
function useSyncUser(shouldRefreshUsersList) {
  const queryClient = useQueryClient()
  const { createNotification } = useNotification()
  const [syncingRowId, setSyncingRowId] = useState(null)

  const { mutate: syncUser, isLoading: isSyncing } = useMutation({
    mutationFn: syncWithFdsAdmin,
    onSuccess: () => {
      createNotification({
        message: 'Successfully synced user with FDS Admin',
        variant: 'success',
      })
      if (shouldRefreshUsersList) {
        queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] })
      }
    },
    onError: error => {
      const message = error?.response?.data?.errors?.[0] || 'Failed to sync user with FDS Admin'
      createNotification({ message, variant: 'error' })
    },
    onSettled: () => setSyncingRowId(null),
  })

  const syncUserForRow = (rowId, serialNumbers) => {
    setSyncingRowId(rowId)
    syncUser(serialNumbers)
  }

  return { syncUser, syncUserForRow, isSyncing, syncingRowId }
}

export default useSyncUser
